/**
 * Phase 27.47 — Google Maps JS loader.
 *
 * Loads the Google Maps JS API exactly once per browser session and
 * returns the resolved `window.google.maps` namespace. Every caller
 * awaits the same promise, so multiple <AddressAutocomplete> mounts
 * don't inject the script multiple times.
 *
 * Degrades gracefully: when the API key is missing, `loadGoogleMaps()`
 * resolves to `null` so components can fall back to a plain input.
 */

const SCRIPT_ID = 'google-maps-js-api';
let cached = null;

export function loadGoogleMaps() {
  if (cached) return cached;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    // Resolve null so callers can render a plain input.
    cached = Promise.resolve(null);
    return cached;
  }

  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    cached = Promise.resolve(window.google.maps);
    return cached;
  }

  cached = new Promise((resolve, reject) => {
    // The loader may already be in-flight from another mount that
    // beat us to the script tag — reuse it instead of double-inserting.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google?.maps || null));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    // `places` is the only library we need for autocomplete.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google?.maps || null);
    script.onerror = (err) => {
      cached = null;  // allow retry on next mount
      reject(err);
    };
    document.head.appendChild(script);
  });
  return cached;
}
